import type { Market, MarketAccount } from '@talos/kyoko';
import {
  AvailabilityEnum,
  type AvailabilityCondition,
  type AvailabilityResult,
  type MarketSelectorItem,
} from '../types';

type BuildMarketSelectorItemParams = {
  market: Market;
  marketAccount?: MarketAccount;
  conditions: AvailabilityCondition[];
};
/**
 * Given a market and maybe a market account, builds you a Market Selector Item. The function
 * verifies your to-be item against the AvailabilityConditions you pass in and applies that AvailabilityResult
 * to the item. As is standard for MarketSelectorItems, Market Accounts are preferred. As in: if both a market and market account is passed,
 * the item will reflect the market account. The market is still available on the item though to help with lookups.
 */
export function buildMarketSelectorItem({
  market,
  marketAccount,
  conditions,
}: BuildMarketSelectorItemParams): MarketSelectorItem {
  const availability = evaluateAvailabilityConditions({ market, marketAccount, conditions });
  return marketAccount
    ? {
        type: 'MarketAccount',
        name: marketAccount.Name,
        displayName: marketAccount.DisplayName,
        market,
        marketAccount,
        availability,
      }
    : {
        type: 'Market',
        name: market.Name,
        displayName: market.DisplayName,
        market,
        availability,
      };
}

export function evaluateAvailabilityConditions({
  market,
  marketAccount,
  conditions,
}: {
  market: Market;
  marketAccount?: MarketAccount;
  conditions: AvailabilityCondition[];
}): AvailabilityResult {
  const availabilityResults = conditions
    .filter(condition => condition.ready)
    .map(condition => condition.condition(market, marketAccount));

  const worstResult: AvailabilityResult = availabilityResults.reduce(
    (worst, next) => {
      return next.availability < worst.availability ? next : worst;
    },
    { availability: AvailabilityEnum.Ok }
  );

  return worstResult;
}

export function isMarketSelectorItemSelectable(item: Pick<MarketSelectorItem, 'availability'>) {
  return item.availability.availability > AvailabilityEnum.Disabled;
}

/** Given an array of availability enums, will return the lowest (worst) availability. If the array is empty, will return OK. */
export function getLowestAvailability(availabilities: AvailabilityEnum[]): AvailabilityEnum {
  return availabilities.reduce((worst, next) => {
    return next < worst ? next : worst;
  }, AvailabilityEnum.Ok);
}

/** Given an array of items, will return the item with the highest availability */
export function getItemWithHighestAvailability(items: MarketSelectorItem[]): MarketSelectorItem | undefined {
  return items.reduce(
    (highestAvailItem, item) => {
      if (highestAvailItem == null) {
        return item;
      }

      return item.availability.availability > highestAvailItem.availability.availability ? item : highestAvailItem;
    },
    undefined as MarketSelectorItem | undefined
  );
}

export function marketSelectorItemComparator(
  a: MarketSelectorItem | undefined,
  b: MarketSelectorItem | undefined,
  direction: '+' | '-'
): number {
  if (direction === '+') {
    // if we're sorting in the other direction, reverse the two variables
    const temp = a;
    a = b;
    b = temp;
  }

  if (!a && !b) {
    return 0;
  }

  if (!a) {
    return -1;
  }

  if (!b) {
    return 1;
  }

  return a.displayName.localeCompare(b.displayName);
}

/**
 * Converts an array of MarketSelectorItem[] into an object of { markets, marketAccounts } to be used with the old market selector
 * Does the logic mapping to include the items which would've been derived in the old market selector mechanisms
 */
export function marketSelectorItemsToOldItems({
  items,
  includeInactiveMarketAccounts,
}: {
  items: MarketSelectorItem[];
  /** Set to true by the old market selector for market data use cases where we want to allow users to select inactive accounts */
  includeInactiveMarketAccounts: boolean;
}) {
  return items.reduce(
    (agg, item) => {
      if (item.availability.availability < AvailabilityEnum.Disabled) {
        return agg;
      }

      if (item.marketAccount) {
        if (!includeInactiveMarketAccounts && item.marketAccount?.Status === 'Inactive') {
          return agg;
        }

        return {
          ...agg,
          marketAccounts: [...agg.marketAccounts, item.marketAccount.Name],
        };
      } else {
        // item is a market item
        return {
          ...agg,
          markets: [...agg.markets, item.market.Name],
        };
      }
    },
    { markets: [] as string[], marketAccounts: [] as string[] }
  );
}
